import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import theme from 'stories/utils/theme'
import { useSpring, animated } from 'react-spring'
import { Body1 } from 'stories/elements/Typography/Text'
import ReactMarkdown from 'react-markdown'

const FAQItem = styled.div<{ isOpen?: boolean }>`
    overflow: hidden;
    background-color: ${theme.colors.transparent};
    transition: all 0.3s ease;
    border-top: 1px solid ${theme.colors.backgroundTransparent};
    box-sizing: border-box;

    &:last-child {
        border-bottom: 1px solid ${theme.colors.backgroundTransparent};
    }
`

interface FAQHeaderProps {
    isOpen: boolean
}

const FAQHeader = styled.div<FAQHeaderProps>`
    padding: 5px 0px 10px;
    display: grid;
    grid-template-columns: 1fr 36px;
    gap: 5px;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    background-color: ${theme.colors.transparent};
    min-height: 68px;
    color: ${theme.colors.white};
    transition: background-color 0.3s ease;
`

const Question = styled.h3`
    margin: 0;
    font-family: var(--font-inter);
    font-size: 17px;
    line-height: 20px;
    font-weight: 650;
    color: #f9f9f9;
    padding: 4px 0;
    letter-spacing: 0.3px;
`

const ToggleButton = styled.div`
    position: relative;
    width: 36px;
    height: 36px;
    background-color: ${theme.colors.backgroundTransparent};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Line = styled.div`
    position: absolute;
    background-color: ${theme.colors.white};
    height: 2px;
    width: 16px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    will-change: transform;
    transition: transform 0.3s ease;
`

const VerticalLine = styled.div`
    position: absolute;
    background-color: ${theme.colors.white};
    width: 2px;
    height: 16px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    will-change: transform;
    transition: transform 0.3s ease;
`

const ContentWrapper = animated.div

const StyledBody1 = styled(Body1)`
    font-family: var(--font-inter);
    font-size: 14px;
    line-height: 20px;
    font-weight: 450;
    letter-spacing: 0.4px;
    position: relative;
    color: ${theme.colors.grayScaleSecondary};
    bottom: 10px;
    strong {
        all: inherit;
        display: inline;
        bottom: 0;
        font-weight: 650;
        color: ${theme.colors.white};
    }
`

const AnimatedBody = animated(StyledBody1)

interface Props {
    question: string
    answer: string
    isOpenByDefault?: boolean
    renderMarkdown?: boolean
}

const FAQComponent = ({ question, answer, isOpenByDefault = false, renderMarkdown = false }: Props) => {
    const [isOpen, setIsOpen] = useState(isOpenByDefault)
    const [hasAnimated, setHasAnimated] = useState(false)
    const [contentHeight, setContentHeight] = useState(0)
    const contentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isOpenByDefault && !hasAnimated) {
            setHasAnimated(true)
        }
    }, [isOpenByDefault, hasAnimated])

    useEffect(() => {
        if (contentRef.current) {
            const height = contentRef.current.scrollHeight
            setContentHeight(height)
        }
    }, [answer])

    useEffect(() => {
        const timer = setTimeout(() => {
            if (contentRef.current) {
                const height = contentRef.current.scrollHeight
                setContentHeight(height)
            }
        }, 10)
        return () => clearTimeout(timer)
    }, [isOpen])

    const animConfig = { tension: 280, friction: 24 }

    const springProps = useSpring({
        height: isOpen ? `${contentHeight}px` : '0px',
        opacity: isOpen ? 1 : 0,
        config: animConfig,
        immediate: isOpenByDefault && !hasAnimated,
    })

    const textSpringProps = useSpring({
        opacity: isOpen ? 1 : 0,
        transform: isOpen ? 'translateY(0px)' : 'translateY(5px)',
        config: animConfig,
        from: {
            opacity: isOpenByDefault && !hasAnimated ? 1 : 0,
            transform: isOpenByDefault && !hasAnimated ? 'translateY(0px)' : 'translateY(5px)',
        },
        immediate: isOpenByDefault && !hasAnimated,
    })

    const iconSpring = useSpring({
        transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
        from: { transform: isOpenByDefault && !hasAnimated ? 'rotate(45deg)' : 'rotate(0deg)' },
        immediate: isOpenByDefault && !hasAnimated,
        config: animConfig,
    })

    return (
        <FAQItem isOpen={isOpen}>
            <FAQHeader onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
                <Question>
                    {renderMarkdown ? (
                        <ReactMarkdown
                            components={{
                                p: ({ children }) => <Question>{children}</Question>,
                            }}
                        >
                            {question}
                        </ReactMarkdown>
                    ) : (
                        question
                    )}
                </Question>
                <ToggleButton>
                    <animated.div style={{ ...iconSpring, position: 'relative', width: '100%', height: '100%' }}>
                        <Line />
                        <VerticalLine />
                    </animated.div>
                </ToggleButton>
            </FAQHeader>
            <ContentWrapper style={springProps}>
                <div ref={contentRef}>
                    {renderMarkdown ? (
                        <AnimatedBody
                            as="div"
                            style={textSpringProps}
                            color={theme.colors.white}
                            fontSize={16}
                            fontFamily="var(--font-inter)"
                            fontWeight={400}
                        >
                            <ReactMarkdown
                                components={{
                                    p: ({ children }) => <StyledBody1>{children}</StyledBody1>,
                                }}
                            >
                                {answer}
                            </ReactMarkdown>
                        </AnimatedBody>
                    ) : (
                        <AnimatedBody
                            style={textSpringProps}
                            color={theme.colors.white}
                            fontSize={16}
                            fontFamily="var(--font-inter)"
                            fontWeight={400}
                        >
                            {answer}
                        </AnimatedBody>
                    )}
                </div>
            </ContentWrapper>
        </FAQItem>
    )
}
export default FAQComponent
